import LoadableResource from 'util/resource/Resource';
import { daysAgo, norwegianDateWithTimestamp } from 'util/dateUtils';
import React, { useMemo, useState } from 'react';
import Card from 'ui/views/cards/Card';
import SectionHeading from 'ui/elements/SectionHeading';
import bluePlanetTheme, { fontSizeSmall } from 'ui/theme/themeBluePlanet';
import DictionaryItem from 'ui/elements/dictionary/DictionaryItem';
import Dictionary from 'ui/elements/dictionary/Dictionary';
import Table from 'ui/elements/tables/Table/Table';
import useTableWithSort from 'ui/elements/tables/Table/useSorting';
import { BackofficeUserDetailsRoleDTO, BackofficeInferredInvestorProfileDTO } from 'types/backoffice';
import { communityUrls, companyUrls } from 'urls';
import { Link } from 'react-router-dom';
import { ICompany } from 'types/company';
import useBackoffice from 'hooks/useBackoffice';
import EnvelopeIcon from 'ui/elements/icons/EnvelopeIcon';
import PopMenu from 'ui/modules/PopMenu';
import useDialogHandler from 'hooks/useDialogHandler';
import EmailEvents from './BackofficeUserEmails';
import BackofficeAPI from 'apis/BackofficeAPI';
import { isBefore } from 'date-fns';
import useResource from 'util/resource/useResource';
import { BackofficeUserDetailsDTO } from 'types/backoffice';
import UserIcon from 'ui/elements/icons/UserIcon';
import UserProfileDialog from 'domain/users/UserProfile/UserProfileDialog';
import TrashIcon from 'ui/elements/icons/TrashIcon';
import useNotify from 'hooks/useNotify';
import useLazyResource from 'util/resource/useLazyResource';
import { invalidate } from 'hooks/useSWR';
import StatsCard from 'ui/views/cards/StatsCard';
import ProgressBar from 'ui/elements/Progress/ProgressBar';
import IndustryChip from 'ui/domain/Chips/IndustryChip';
import styled from '@emotion/styled';
import { contentSpacing, halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import ChevronDownIcon from 'ui/elements/icons/ChevronDownIcon';
import ChevronUpIcon from 'ui/elements/icons/ChevronUpIcon';
import Tooltip from 'ui/elements/Tooltip';

const analyticsTrackingStartDate = '2023-12-07';

interface Props {
  userId: number;
  cwUserId: UUID;
  className?: string;
}
export function BackofficeUserDetails({ cwUserId, userId, className }: Props) {
  const backoffice = useBackoffice();

  return backoffice.showBackofficeInfoOnProfiles ? (
    <Inner userId={userId} className={className} cwUserId={cwUserId} />
  ) : null;
}

function Inner({ userId, className, cwUserId }: Props) {
  const { resource: detailsResource } = useResource<BackofficeUserDetailsDTO>(BackofficeAPI.users.get(userId));
  const mailDialog = useDialogHandler(false);
  const [profileDialogIsOpen, setProfileDialogIsOpen] = useState(false);
  const notify = useNotify();

  const [requestDeletion, isRequestingDeletion] = useLazyResource(
    (userId: UUID) => BackofficeAPI.users.deletionRequests.post(userId),
    {
      onSuccess: () => {
        invalidate(BackofficeAPI.users.deletionRequests.get);
        notify('success', 'Request sent');
      },
      onFailure: () => notify('error', 'Could not request deletion'),
    },
  );

  return (
    <>
      <Card
        className={className ?? 'u-section-spacing-top'}
        style={{ backgroundColor: bluePlanetTheme.bluePlanetPalette.yellow.medium }}
      >
        <SectionHeading heading="Backoffice">
          <PopMenu
            items={[
              {
                icon: <EnvelopeIcon />,
                text: <>Emails</>,
                onClick: mailDialog.open,
              },
              {
                icon: <UserIcon />,
                text: <>View profile</>,
                onClick: () => setProfileDialogIsOpen(true),
              },
              {
                icon: <TrashIcon />,
                text: <>Request deletion</>,
                onClick: () => (!isRequestingDeletion ? requestDeletion(cwUserId) : undefined),
              },
            ]}
          />
        </SectionHeading>
        <LoadableResource resource={detailsResource}>
          {details => (
            <div>
              <Dictionary>
                <DictionaryItem label="Last login">
                  {daysAgo(details.lastLogin)}
                  {details.lastLogin && <> - {norwegianDateWithTimestamp(details.lastLogin)}</>}
                </DictionaryItem>
                <DictionaryItem label="Last visit">
                  {details.email.endsWith('@crowdworks.it') ? (
                    <>No visitor tracking for internal users</>
                  ) : (
                    <>
                      {details.lastLogin &&
                      !details.lastVisit &&
                      isBefore(new Date(details.lastLogin), new Date(analyticsTrackingStartDate)) ? (
                        <i>No visitor tracking before {analyticsTrackingStartDate}.</i>
                      ) : (
                        <>
                          {daysAgo(details.lastVisit)}
                          {details.lastVisit && <> - {norwegianDateWithTimestamp(details.lastVisit)}</>}
                        </>
                      )}
                    </>
                  )}
                </DictionaryItem>
                <DictionaryItem label="Signup URL">{details.signupUrl ?? 'No data'}</DictionaryItem>
                <DictionaryItem label="Epost">{details.email} </DictionaryItem>
              </Dictionary>

              {details.inferredInvestorProfile && (
                <InvestmentPatternsSection investorProfile={details.inferredInvestorProfile} />
              )}

              <SectionHeading heading="Company Roles" className="u-content-spacing-top" />
              <RoleListing roles={details.companyRoles} href={entity => companyUrls.overview(entity.slug, 'profile')} />

              <SectionHeading heading="Community Roles" className="u-content-spacing-top" />
              <RoleListing roles={details.communityRoles} href={entity => communityUrls.overview(entity.slug)} />
            </div>
          )}
        </LoadableResource>
        {mailDialog.isOpen && <EmailEvents userId={userId} onClose={mailDialog.close} />}
      </Card>
      <UserProfileDialog
        isOpen={profileDialogIsOpen}
        onClose={() => setProfileDialogIsOpen(false)}
        cwUserId={cwUserId}
      ></UserProfileDialog>
    </>
  );
}

function RoleListing({ roles, href }: { roles: BackofficeUserDetailsRoleDTO[]; href: (entity: ICompany) => string }) {
  const { sortOptions, onSort } = useTableWithSort(undefined);
  const sortFn: {
    [key: string]: (a: BackofficeUserDetailsRoleDTO, b: BackofficeUserDetailsRoleDTO) => number;
  } = {
    entity: (a, b) => a.entity.name.localeCompare(b.entity.name),
    role: (a, b) => a.role.localeCompare(b.role),
    givenAt: (a, b) => a.roleGivenAt.localeCompare(b.roleGivenAt),
  };
  const sortedRoles = useMemo(() => {
    return sortOptions
      ? roles.sort((a, b) => {
          const result = sortFn[sortOptions.sortBy](a, b);
          return sortOptions.sortOrder === 'asc' ? result : result * -1;
        })
      : roles;
  }, [roles, sortOptions]);
  return (
    <Table isLoading={false} infoMessages={{ noResults: 'None' }} onSort={onSort} sortOptions={sortOptions}>
      <Table.Header>
        <Table.HeaderCell isSortable={true} sortBy="entity" style={{ width: '60%' }}>
          Name
        </Table.HeaderCell>
        <Table.HeaderCell isSortable={true} sortBy="role">
          Role
        </Table.HeaderCell>
        <Table.HeaderCell isSortable={true} sortBy="givenAt">
          Given at
        </Table.HeaderCell>
      </Table.Header>
      <Table.Body>
        {sortedRoles.map(role => (
          <Table.Row key={`${role.role}-${role.entity.id}`}>
            <Table.DataCell>
              <Link to={href(role.entity)} target="_blank">
                {role.entity.name}
              </Link>
            </Table.DataCell>
            <Table.DataCell>{role.role}</Table.DataCell>
            <Table.DataCell>
              <div className="u-align-left">{norwegianDateWithTimestamp(role.roleGivenAt)}</div>
            </Table.DataCell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${halfSpacing};
  margin-bottom: ${contentSpacing};
`;

const CompactSection = styled.div`
  margin-bottom: ${contentSpacing};
`;

const CompactPreferenceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${quarterSpacing};
  padding: ${quarterSpacing} ${halfSpacing};
  background-color: ${bluePlanetTheme.bluePlanetPalette.grey.veryLight};
  border-radius: 4px;
  font-size: ${fontSizeSmall};
`;

const CompactLabel = styled.div`
  flex: 1;
  margin-right: ${halfSpacing};
  font-weight: 500;
`;

const CompactProgress = styled.div`
  flex: 1.5;
  margin-right: ${halfSpacing};
`;

const CompactWeight = styled.div`
  min-width: 40px;
  text-align: right;
  font-weight: 600;
  color: ${bluePlanetTheme.bluePlanetPalette.indigo.main};
  font-size: 0.75rem;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${contentSpacing};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ExpandableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: ${bluePlanetTheme.bluePlanetPalette.blue.main};
  }
`;

const ExpandableContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => (props.isExpanded ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const CompanySizeItem = styled.span`
  cursor: help;
  border-bottom: 1px dotted ${bluePlanetTheme.bluePlanetPalette.grey.main};

  &:hover {
    color: ${bluePlanetTheme.bluePlanetPalette.blue.main};
  }
`;

function InvestmentPatternsSection({ investorProfile }: { investorProfile: BackofficeInferredInvestorProfileDTO }) {
  const { patterns, confidence, lastUpdated } = investorProfile;
  const [industriesExpanded, setIndustriesExpanded] = useState(false);

  const topIndustries = patterns.preferredIndustries.sort((a, b) => b.weight - a.weight).slice(0, 3);

  const allIndustries = patterns.preferredIndustries.sort((a, b) => b.weight - a.weight);

  const topFundingStages = patterns.preferredFundingStages.sort((a, b) => b.weight - a.weight).slice(0, 2);

  const topProductStages = patterns.preferredProductStages.sort((a, b) => b.weight - a.weight).slice(0, 2);

  const topCountries = Object.entries(patterns.preferredCountryCodes)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const topRegions = Object.entries(patterns.preferredRegions)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 2);

  const hasConfidenceData = Object.keys(confidence).length > 0;
  const highestConfidence = hasConfidenceData ? Object.entries(confidence).sort(([, a], [, b]) => b - a)[0] : null;

  // Company size descriptions
  const getCompanySizeDescription = (size: string): string => {
    switch (size) {
      case 'Startup':
        return '1-10 employees';
      case 'Small':
        return '11-50 employees';
      case 'Medium':
        return '51-200 employees';
      case 'Large':
        return '201-1,000 employees';
      case 'Enterprise':
        return '1,000+ employees';
      default:
        return size;
    }
  };

  const formatCompanySizes = () => {
    return Object.entries(patterns.preferredCompanySizes)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([size, weight], index, array) => (
        <span key={size}>
          <Tooltip title={getCompanySizeDescription(size)} color="dark">
            <CompanySizeItem>
              {size} ({Math.round(weight * 100)}%)
            </CompanySizeItem>
          </Tooltip>
          {index < array.length - 1 ? ' • ' : ''}
        </span>
      ));
  };

  return (
    <>
      <SectionHeading heading="Investment Patterns" className="u-content-spacing-top" />
      <div
        style={{ fontSize: '0.75rem', color: bluePlanetTheme.bluePlanetPalette.grey.main, marginBottom: halfSpacing }}
      >
        Updated: {norwegianDateWithTimestamp(lastUpdated)}
        {highestConfidence && ` • Confidence: ${Math.round(highestConfidence[1] * 100)}%`}
      </div>

      {/* Compact Activity Stats */}
      <StatsGrid>
        <StatsCard title="Total Activity" value={patterns.totalInteractions} color="blue" padding="half" />
        <StatsCard title="Investments" value={patterns.investmentCount} color="orange" padding="half" />
        <StatsCard
          title="Following"
          value={patterns.followCount + patterns.shortlistCount}
          color="default"
          padding="half"
        />
      </StatsGrid>

      <TwoColumnGrid>
        {/* Left Column - Industries & Stages */}
        <div>
          {/* Preferred Industries */}
          {topIndustries.length > 0 && (
            <CompactSection>
              <ExpandableHeader onClick={() => setIndustriesExpanded(!industriesExpanded)}>
                <h5
                  style={{
                    marginBottom: '0',
                    color: bluePlanetTheme.bluePlanetPalette.indigo.main,
                    fontSize: '0.875rem',
                  }}
                >
                  Top Industries ({allIndustries.length})
                </h5>
                {industriesExpanded ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
              </ExpandableHeader>

              {/* Always show top 3 */}
              <div style={{ marginTop: '4px' }}>
                {topIndustries.map(item => (
                  <CompactPreferenceItem key={item.industry.id}>
                    <CompactLabel>{item.industry.name}</CompactLabel>
                    <CompactProgress>
                      <ProgressBar percentage={Math.round(item.weight * 100)} color="blue" />
                    </CompactProgress>
                    <CompactWeight>{Math.round(item.weight * 100)}%</CompactWeight>
                  </CompactPreferenceItem>
                ))}
              </div>

              {/* Expandable section for remaining industries */}
              <ExpandableContent isExpanded={industriesExpanded}>
                <div style={{ marginTop: '8px' }}>
                  {allIndustries.slice(3).map(item => (
                    <CompactPreferenceItem key={item.industry.id}>
                      <CompactLabel>{item.industry.name}</CompactLabel>
                      <CompactProgress>
                        <ProgressBar percentage={Math.round(item.weight * 100)} color="blue" />
                      </CompactProgress>
                      <CompactWeight>{Math.round(item.weight * 100)}%</CompactWeight>
                    </CompactPreferenceItem>
                  ))}

                  {/* Show all industry chips when expanded */}
                  <ChipsRow style={{ marginTop: '8px' }}>
                    {allIndustries.map(item => (
                      <IndustryChip key={item.industry.id} label={item.industry.name} fontSize="small" />
                    ))}
                  </ChipsRow>
                </div>
              </ExpandableContent>
            </CompactSection>
          )}

          {/* Funding & Product Stages Combined */}
          {(topFundingStages.length > 0 || topProductStages.length > 0) && (
            <CompactSection>
              <h5
                style={{
                  marginBottom: '4px',
                  color: bluePlanetTheme.bluePlanetPalette.indigo.main,
                  fontSize: '0.875rem',
                }}
              >
                Preferred Stages
              </h5>
              {topFundingStages.map(item => (
                <CompactPreferenceItem key={`funding-${item.fundingStage.id}`}>
                  <CompactLabel>💰 {item.fundingStage.name}</CompactLabel>
                  <CompactProgress>
                    <ProgressBar percentage={Math.round(item.weight * 100)} color="yellow" />
                  </CompactProgress>
                  <CompactWeight>{Math.round(item.weight * 100)}%</CompactWeight>
                </CompactPreferenceItem>
              ))}
              {topProductStages.map(item => (
                <CompactPreferenceItem key={`product-${item.productStage.id}`}>
                  <CompactLabel>🚀 {item.productStage.name}</CompactLabel>
                  <CompactProgress>
                    <ProgressBar percentage={Math.round(item.weight * 100)} color="green" />
                  </CompactProgress>
                  <CompactWeight>{Math.round(item.weight * 100)}%</CompactWeight>
                </CompactPreferenceItem>
              ))}
            </CompactSection>
          )}
        </div>

        {/* Right Column - Geography */}
        <div>
          {/* Countries & Regions Combined */}
          {(topCountries.length > 0 || topRegions.length > 0) && (
            <CompactSection>
              <h5
                style={{
                  marginBottom: '4px',
                  color: bluePlanetTheme.bluePlanetPalette.indigo.main,
                  fontSize: '0.875rem',
                }}
              >
                Geographic Focus
              </h5>
              {topCountries.map(([country, weight]) => (
                <CompactPreferenceItem key={`country-${country}`}>
                  <CompactLabel>🌍 {country}</CompactLabel>
                  <CompactProgress>
                    <ProgressBar percentage={Math.round(weight * 100)} color="blue" />
                  </CompactProgress>
                  <CompactWeight>{Math.round(weight * 100)}%</CompactWeight>
                </CompactPreferenceItem>
              ))}
              {topRegions.map(([region, weight]) => (
                <CompactPreferenceItem key={`region-${region}`}>
                  <CompactLabel>📍 {region}</CompactLabel>
                  <CompactProgress>
                    <ProgressBar percentage={Math.round(weight * 100)} color="green" />
                  </CompactProgress>
                  <CompactWeight>{Math.round(weight * 100)}%</CompactWeight>
                </CompactPreferenceItem>
              ))}
            </CompactSection>
          )}

          {/* Company Sizes with Tooltips */}
          {Object.keys(patterns.preferredCompanySizes).length > 0 && (
            <CompactSection>
              <h5
                style={{
                  marginBottom: '4px',
                  color: bluePlanetTheme.bluePlanetPalette.indigo.main,
                  fontSize: '0.875rem',
                }}
              >
                Company Sizes
              </h5>
              <div style={{ fontSize: '0.75rem', color: bluePlanetTheme.bluePlanetPalette.grey.main }}>
                {formatCompanySizes()}
              </div>
            </CompactSection>
          )}
        </div>
      </TwoColumnGrid>
    </>
  );
}
